/*
 * Paper 2 multiple-choice question sets.
 *
 * These cover the NON-coding parts of a real Paper 2 question — "state the
 * output", "state the Big O", trace tables, and the short explain/outline items
 * (private modifier, aggregation vs composition, file-handling steps) — which
 * the construct scenarios (P2.1–P2.5) can't. They are marked client-side
 * (practice, not a graded exam), rendered by Paper2Quiz, and surfaced under the
 * "MCQs" group of the Paper 2 section.
 *
 * Shape:
 *   { id, title, description, questions: [
 *       { id, question, code?, options: [string], answer: string, explanation }
 *   ] }
 *
 * `answer` must be one of `options` exactly. `code`, when present, is shown as a
 * fixed-width snippet above the options.
 */

export const PAPER2_MCQ_SETS = {
  p2_core_skills: {
    id:    'p2_core_skills',
    title: 'Core skills',
    description: 'Complexity, tracing, stacks and OOP — the quick questions a Paper 2 asks around the code you write.',
    questions: [
      {
        id: 'bigo_linear',
        question: 'An algorithm uses a single loop that runs once for each of the n items in a list. What is its time complexity?',
        options: ['O(1)', 'O(log n)', 'O(n)', 'O(n²)'],
        answer: 'O(n)',
        explanation: 'The work grows in direct proportion to the number of items, so one pass over n items is O(n).',
      },
      {
        id: 'bigo_nested',
        question: 'A function has a loop inside another loop, and each loop runs n times. What is its time complexity?',
        options: ['O(n)', 'O(2n)', 'O(n²)', 'O(log n)'],
        answer: 'O(n²)',
        explanation: 'For every one of the n outer passes the inner loop runs n times, giving n × n = O(n²).',
      },
      {
        id: 'trace_nested_count',
        question: 'What does this code print?',
        code: 'total = 0\nfor i in range(1, 4):\n    for j in range(i):\n        total = total + 1\nprint(total)',
        options: ['3', '6', '9', '12'],
        answer: '6',
        explanation: 'The inner loop runs 1 time when i = 1, 2 times when i = 2 and 3 times when i = 3, so total = 1 + 2 + 3 = 6.',
      },
      {
        id: 'trace_2d_index',
        question: 'What is the output of this code?',
        code: 'grid = [[2, 7, 1], [8, 9, 0]]\nprint(grid[1][2])',
        options: ['1', '8', '9', '0'],
        answer: '0',
        explanation: 'grid[1] is the second row [8, 9, 0]; index [2] is its third item, 0.',
      },
      {
        id: 'stack_top',
        question: 'A stack starts empty. After push(3), push(8), pop(), push(5), which value is on top of the stack?',
        options: ['3', '8', '5', 'the stack is empty'],
        answer: '5',
        explanation: 'push(3) → [3]; push(8) → [3, 8]; pop() removes 8 → [3]; push(5) → [3, 5]. The top is the last item pushed, 5.',
      },
      {
        id: 'oop_private',
        question: 'A class attribute is declared private. What is a consequence of this?',
        options: [
          'It can be read and changed directly from any other class',
          'It can only be accessed through the class’s own methods',
          'It is automatically shared by every object of the class',
          'It can no longer be used inside the class itself',
        ],
        answer: 'It can only be accessed through the class’s own methods',
        explanation: 'Private restricts access to within the class, so other code must go through the class’s public methods (encapsulation).',
      },
      {
        id: 'oop_aggregation',
        question: 'A Flight object holds an array of Passenger objects, but the passengers still exist if the flight is deleted. This relationship is best described as:',
        options: ['Inheritance', 'Aggregation', 'Composition', 'Polymorphism'],
        answer: 'Aggregation',
        explanation: 'In aggregation the parts can exist independently of the whole. In composition they could not outlive it.',
      },
      {
        id: 'file_steps_order',
        question: 'To read data from a text file, which is the correct order of steps?',
        options: [
          'Open the file, read the lines, close the file',
          'Read the lines, open the file, close the file',
          'Close the file, open the file, read the lines',
          'Open the file, close the file, read the lines',
        ],
        answer: 'Open the file, read the lines, close the file',
        explanation: 'A file must be opened before it can be read, and closed afterwards to release it and save any changes.',
      },
    ],
  },
};

/** Look up a set by id (used by Paper2Quiz). */
export function getPaper2McqSet(setId) {
  return PAPER2_MCQ_SETS[setId] || null;
}
